// lib
import React, { useState, useRef, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'

// components
import { Dropdown } from 'primereact/dropdown'
import { MultiSelect } from 'primereact/multiselect'

import MultiSelectHeader from 'primereact-raytm-theme/MultiSelectHeader'
import MultiSelectFooter from 'primereact-raytm-theme/MultiSelectFooter'

import { optionGroupTemplate } from '@aytm/app/stats/page/components/lib/templates'

import { default as ControlsContainer } from '@aytm/app/stats/page/question-views/components/component/Controls'

const Controls = ({
  model,
  data,
  collapsed,
  disabled,
  onToggle,
  onChange,
  getTopsOptions,
  getLabelsOptions,
  getImagesOptions,
  getAnswerVariantsOptions
}) => {
  const [source, setSource] = useState(null)

  const [top, setTop] = useState(null)
  const [label, setLabel] = useState(null)
  const [image, setImage] = useState(null)

  const [answerVariants, setAnswerVariants] = useState(null)

  const sourceRef = useRef(null)
  const optionsRef = useRef(null)

  const sourceGroup = useMemo(() => (null !== source && source.split('.').slice(0, 2).join('.')) || null, [source])

  const sourceGroupRef = useRef(null)
  sourceGroupRef.current = sourceGroup

  const topsOptions = useMemo(() => (null !== source ? getTopsOptions(source) : []), [source, getTopsOptions])
  const labelsOptions = useMemo(() => (null !== source ? getLabelsOptions(source) : []), [source, getLabelsOptions])
  const imagesOptions = useMemo(() => (null !== source ? getImagesOptions(source) : []), [source, getImagesOptions])

  const answerVariantsOptions = useMemo(
    () => (null !== source ? getAnswerVariantsOptions(source) : []),
    [source, getAnswerVariantsOptions]
  )

  const itemTemplate = option =>
    option.disabled && option.icon == undefined ? (
      <span dangerouslySetInnerHTML={{ __html: option.text }}></span>
    ) : (
      <span className={`p-item-label p-lvl-${option.lvl || 0}`}>
        {option.icon ? <i className={`p-item-icon icon-aytmfd-${option.icon}`} /> : null}
        <span dangerouslySetInnerHTML={{ __html: option.text }}></span>
      </span>
    )
  const valueTemplate = (option, _props) =>
    option ? (
      <>
        {option.icon ? <i className={`p-item-icon icon-aytmfd-${option.icon}`} /> : null}
        <span dangerouslySetInnerHTML={{ __html: option.label || option.text }}></span>
      </>
    ) : (
      <span dangerouslySetInnerHTML={{ __html: _props.placeholder || 'Select...' }}></span>
    )

  const buttonLabelTemplate = event => {
    const text =
      event != undefined && event.value != undefined && 0 < event.value.length
        ? event.options.length === event.value.length
          ? 'All'
          : answerVariantsOptions
              .filter(o => event.value.includes(o.key))
              .map(o => o.label)
              .join(', ')
        : 'Select...'
    return <span dangerouslySetInnerHTML={{ __html: text || '&nbsp;' }}></span>
  }

  useEffect(() => setTop(0 < topsOptions.length ? topsOptions[0].key : null), [topsOptions])
  useEffect(() => {
    const index = labelsOptions.findIndex(v => v != undefined && v.default)
    setLabel(0 < labelsOptions.length ? labelsOptions[(-1 < index && index) || 0].key : null)
  }, [labelsOptions])
  useEffect(() => {
    const index = imagesOptions.findIndex(v => v != undefined && v.default)
    setImage(0 < imagesOptions.length ? imagesOptions[(-1 < index && index) || 0].key : null)
  }, [imagesOptions])

  useEffect(
    () => setAnswerVariants(0 < answerVariantsOptions.length ? [answerVariantsOptions[0].key] : null),
    [answerVariantsOptions]
  )

  useEffect(
    () => null !== source && onChange('source', { top: null !== top ? top : source, label, image, answerVariants }),
    [source, top, label, image, answerVariants, onChange]
  )

  useEffect(() => {
    if (null === source) return

    const option = data.controls.source.options
      .flatMap(v => (v != undefined && v.items) || [])
      .find(v => source == v.key)

    if (option?.variant === model.variant || (!option?.variant && ['rb', 'cb'].includes(model.variant))) return

    setSource(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model.variant, data.controls.source.options])

  return (
    <ControlsContainer data={data} collapsed={collapsed} onToggle={onToggle}>
      {0 < data.controls.source.options.flatMap(v => (v != undefined && v.items) || []).length ? (
        <div ref={sourceRef} className="p-composite-group p-fluid p-field p-col-5">
          <Dropdown
            className={`p-dropdown-rounded p-col-${0 < topsOptions.length ? 6 : 12}`}
            placeholder={data.controls.source.placeholder}
            value={source}
            options={data.controls.source.options}
            optionValue="key"
            optionLabel="text"
            optionGroupLabel="text"
            optionGroupChildren="items"
            itemTemplate={itemTemplate}
            valueTemplate={valueTemplate}
            optionGroupTemplate={optionGroupTemplate}
            filter={true}
            filterBy="search,text"
            filterPlaceholder="Search items"
            resetFilterOnHide={true}
            filterInputAutoFocus={false}
            fixedDropdownDir="bottom"
            appendTo={sourceRef.current || 'self'}
            disabled={disabled || data.controls.source.disabled}
            onChange={disabled || data.controls.source.disabled ? null : event => setSource(event.value)}
          />
          {0 < topsOptions.length && (
            <Dropdown
              className="p-dropdown-rounded p-col-6"
              value={top}
              options={topsOptions}
              optionValue="key"
              optionLabel="text"
              itemTemplate={itemTemplate}
              valueTemplate={valueTemplate}
              fixedDropdownDir="bottom"
              appendTo={sourceRef.current || 'self'}
              disabled={disabled || data.controls.source.disabled}
              onChange={disabled || data.controls.source.disabled ? null : event => setTop(event.value)}
            />
          )}
        </div>
      ) : null}
      {0 < labelsOptions.length ? (
        <div ref={optionsRef} className="p-composite-group p-fluid p-field p-col-6">
          <Dropdown
            className={`p-dropdown-rounded p-col-${0 < imagesOptions.length ? 6 : 12}`}
            value={label}
            options={labelsOptions}
            optionValue="key"
            optionLabel="text"
            itemTemplate={itemTemplate}
            valueTemplate={valueTemplate}
            fixedDropdownDir="bottom"
            appendTo={optionsRef.current || 'self'}
            disabled={disabled || data.controls.source.disabled}
            onChange={disabled || data.controls.source.disabled ? null : event => setLabel(event.value)}
          />
          {0 < imagesOptions.length && (
            <Dropdown
              className="p-dropdown-rounded p-col-6"
              value={image}
              options={imagesOptions}
              optionValue="key"
              optionLabel="text"
              itemTemplate={itemTemplate}
              valueTemplate={valueTemplate}
              fixedDropdownDir="bottom"
              appendTo={optionsRef.current || 'self'}
              disabled={disabled || data.controls.source.disabled}
              onChange={disabled || data.controls.source.disabled ? null : event => setImage(event.value)}
            />
          )}
        </div>
      ) : 0 < answerVariantsOptions.length ? (
        <div className="p-field p-col-5">
          <MultiSelect
            className="p-multiselect-rounded"
            placeholder="Select..."
            value={answerVariants || []}
            options={answerVariantsOptions}
            optionValue="key"
            optionLabel="text"
            itemTemplate={itemTemplate}
            buttonLabelTemplate={buttonLabelTemplate}
            panelHeaderTemplate={MultiSelectHeader}
            panelFooterTemplate={MultiSelectFooter}
            fixedDropdownDir="bottom"
            disabled={disabled || data.controls.source.disabled}
            onChange={disabled || data.controls.source.disabled ? null : event => setAnswerVariants(event.value)}
          />
        </div>
      ) : (
        <div className="p-field p-col-5 p-float-label">
          <Dropdown
            id={`variant-${model.id}`}
            className="p-dropdown-rounded"
            value={model.variant}
            options={data.controls.variant.options}
            optionValue="key"
            optionLabel="text"
            itemTemplate={itemTemplate}
            valueTemplate={valueTemplate}
            fixedDropdownDir="bottom"
            disabled={disabled || data.controls.variant.disabled}
            onChange={disabled || data.controls.variant.disabled ? null : event => onChange('variant', event.value)}
          />
          {0 < data.controls.variant.note.length && <label>{data.controls.variant.note}</label>}
        </div>
      )}
    </ControlsContainer>
  )
}

Controls.propTypes = {
  model: PropTypes.object,
  data: PropTypes.object,
  collapsed: PropTypes.bool,
  disabled: PropTypes.bool,
  onToggle: PropTypes.func,
  onChange: PropTypes.func,
  getTopsOptions: PropTypes.func,
  getLabelsOptions: PropTypes.func,
  getImagesOptions: PropTypes.func,
  getAnswerVariantsOptions: PropTypes.func
}

Controls.displayName = 'Controls'

export default Controls
